import { useState } from 'react';
import {
  Box,
  Button,
  Collapse,
  ListItemIcon,
  MenuItem,
  Select,
  Tooltip,
  useTheme,
} from '@mui/material';
import EmojiSymbolsIcon from '@mui/icons-material/EmojiSymbols';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { CustomToolbarBtnProps } from '../types/customToolbarBtnTypes';

import { symbols } from './symbols';

export default function SpecialCharacters({
  editor,
  disabled,
  tooltipPlacement,
}: CustomToolbarBtnProps) {
  const theme = useTheme();
  const [open, setOpen] = useState(false);
  const [openCategory, setOpenCategory] = useState<string | null>(null);

  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen);
  };

  const handleCategoryToggle = (
    category: string,
    event: React.MouseEvent<HTMLLIElement, MouseEvent>,
  ) => {
    event.stopPropagation();
    setOpenCategory((prevCategory) => (prevCategory === category ? null : category));
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setOpenCategory(null);
  };

  return (
    <Select
      displayEmpty
      open={open}
      onClose={handleClose}
      onOpen={handleToggle}
      value=""
      disabled={disabled}
      renderValue={() => (
        <Button
          sx={{ minWidth: 'auto' }}
          disabled={disabled}
        >
          <Tooltip
            title="Special characters"
            placement={tooltipPlacement}
            disableInteractive
            slotProps={{
              popper: { keepMounted: true },
            }}
          >
            <EmojiSymbolsIcon />
          </Tooltip>
        </Button>
      )}
      MenuProps={{
        variant: 'menu',
        sx: {
          maxHeight: 475,
          '& .MuiPaper-root': {
            width: 320,
            maxWidth: '100%',
            '&::-webkit-scrollbar': {
              width: 6,
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor:
                theme.palette.mode === 'dark'
                  ? theme.palette.grey[800]
                  : theme.palette.grey[300],
              borderRadius: theme.shape.borderRadius,
            },
            '&::-webkit-scrollbar-track': {
              borderRadius: theme.shape.borderRadius,
            },
          },
        },
      }}
      sx={{
        '& .MuiOutlinedInput-notchedOutline': {
          border: 'none', // Remove select border
        },
        '& .MuiSelect-select': {
          p: '0 !important',
        },
        '& .MuiSelect-icon': {
          display: 'none', // Hide the dropdown arrow
        },
        '& .MuiSelect-select.MuiSelect-outlined': {
          pr: '0 !important',
        }, // Remove the dropdown arrow padding
      }}
    >
      {Object.entries(symbols).map(([category, items]) => (
        <div key={category}>
          <MenuItem onClick={(event) => handleCategoryToggle(category, event)}>
            <ListItemIcon>
              {openCategory === category ? <ExpandMoreIcon /> : <ChevronRightIcon />}
            </ListItemIcon>
            <Box>{category}</Box>
          </MenuItem>
          <Collapse
            in={openCategory === category}
            timeout="auto"
            sx={{ pl: 4 }}
            unmountOnExit
          >
            {items.map(([symbol, entity, label]) => (
              <MenuItem
                key={entity}
                onClick={() => {
                  handleClose();
                  editor?.chain().focus().insertContent(symbol).run();
                }}
              >
                <Box>{symbol}</Box>
                <Box sx={{ textTransform: 'capitalize', ml: 1 }}>{label}</Box>
              </MenuItem>
            ))}
          </Collapse>
        </div>
      ))}
    </Select>
  );
}
